import React from 'react';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { parseError } from '../../../services/utils';
import { useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import css from './text.module.css';
import FormField from '../../common/form-field';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ReadInput } from './read-input';

type TextProps = {
  inline?: boolean;
  sampleText?: string;
  childrenText?: React.ReactNode;
  placeholder?: string;
  isNoteForm?: boolean;
  column?: boolean;
  requiredShow?: boolean;
};

export default function Text(
  name: string,
  label: string = null,
  icon: IconDefinition = null,
  properties: TextProps = {},
  horizontal?: boolean,
  className?: string,
  column: boolean = false,
  childrenText?: React.ReactNode
): FormComponent {
  return ({ ...rest }) => (
    <TextComponent
      name={name}
      label={label}
      icon={icon}
      inline={false}
      horizontal={horizontal}
      column={column}
      {...properties}
      {...rest}
      childrenText={childrenText}
    />
  );
}

export function TextComponent({
  name,
  label,
  icon,
  parentName,
  readonly,
  disabled,
  required,
  inline,
  sampleText,
  horizontal = false,
  column,
  childrenText,
  placeholder,
  isNoteForm,
  requiredShow = false,
}: FormComponentProps & {
  name: string;
  label: string;
  icon?: IconDefinition;
  required?: string[];
  inline?: boolean;
  sampleText?: string;
  horizontal?: boolean;
  column?: boolean;
  childrenText?: React.ReactNode;
  placeholder?: string;
  isNoteForm?: boolean;
  requiredShow?: boolean;
}) {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();
  const inputName = parentName ? `${parentName}.${name}` : name;
  const error = parseError(inputName, errors);

  if (readonly) {
    return (
      <ReadInput
        inline={inline}
        name={inputName}
        label={label ? t(label) : t(name)}
        column={column}
        childrenText={childrenText}
        horizontal={horizontal}
      />
    );
  } else {
    return (
      <FormField
        inline={inline}
        label={label ? t(label) : t(name)}
        labelFor={inputName}
        required={required?.includes(inputName) || requiredShow}
        error={error ? error.message : null}
        horizontal={horizontal}
        column={column}
        isNoteForm={isNoteForm}
      >
        <TextInputComponent
          name={inputName}
          readonly={readonly}
          disabled={disabled}
          icon={icon}
          placeholder={placeholder}
          isNoteForm={isNoteForm}
          error={!!error}
        />
        {childrenText}
        {sampleText && <span className={css['sample-text']}>{sampleText}</span>}
      </FormField>
    );
  }
}

interface TextType {
  name: string;
  readonly?: boolean;
  disabled?: boolean;
  icon?: IconDefinition;
  placeholder?: string;
  isNoteForm?: boolean;
  error?: boolean;
}

export function TextInputComponent({
  name,
  readonly,
  disabled,
  icon,
  placeholder,
  isNoteForm,
  error,
}: TextType) {
  const { register } = useFormContext();
  const iconElement = icon ? <FontAwesomeIcon icon={icon} /> : '';
  return (
    <div className={css['input-container']}>
      <input
        type={'text'}
        name={name}
        className={
          isNoteForm
            ? error
              ? css['input-note-error']
              : css['input-note']
            : css['input']
        }
        {...register(name)}
        readOnly={readonly ?? false}
        disabled={disabled ?? false}
        placeholder={placeholder}
      />
      {iconElement ?? ''}
    </div>
  );
}
